// An expanded row's detail, spanning every column. The detail keeps to the width of the table region the
// reader can see and scrolls sideways inside it, so wide content never widens the page; its height and
// opacity ease in and out (motion.js). Focus inside a detail that goes away returns to the row's chevron.

import React, { useLayoutEffect, useRef } from 'react'
import PropTypes from 'prop-types'

import { useTable } from '../context'
import { useVisibleWidth } from '../states/useVisibleWidth'
import { animateDetail } from './motion'

const noop = () => {}

export const DetailRow = ({
  id,
  row,
  leaving = false,
  onExited = noop,
  returnFocus = noop,
}) => {
  const { layout, renderDetail } = useTable()
  const clipRef = useRef(null)
  const viewRef = useRef(null)
  const entered = useRef(false)
  const width = useVisibleWidth(viewRef)

  const latest = useRef({ onExited, returnFocus })
  latest.current = { onExited, returnFocus }

  const keepFocus = () => {
    const el = clipRef.current
    if (el && el.contains(document.activeElement)) latest.current.returnFocus()
  }

  // runs before the row leaves the document, while its focused descendant is still there
  useLayoutEffect(() => keepFocus, [])

  useLayoutEffect(() => {
    const entering = !entered.current
    entered.current = true
    if (leaving) keepFocus()
    return animateDetail(clipRef.current, {
      entering,
      leaving,
      onDone: leaving ? () => latest.current.onExited() : noop,
    })
  }, [leaving])

  return (
    <tr
      className="me-table-detail"
      id={id}
      aria-hidden={leaving ? 'true' : undefined}
    >
      <td colSpan={layout.columnCount}>
        <div className="me-table-detail-clip" ref={clipRef}>
          <div
            className="me-table-detail-view"
            ref={viewRef}
            style={width ? { '--me-table-detail-w': `${width}px` } : null}
          >
            <div className="me-table-detail-body">{renderDetail(row)}</div>
          </div>
        </div>
      </td>
    </tr>
  )
}

DetailRow.propTypes = {
  id: PropTypes.string.isRequired,
  row: PropTypes.object.isRequired,
  // the detail is on its way out; it stays in the document until onExited
  leaving: PropTypes.bool,
  onExited: PropTypes.func,
  // moves focus to the row's chevron
  returnFocus: PropTypes.func,
}
